import type { Task } from "../api/types";

/* The portal's Tasks list: tasks only (no stories), completed ones hidden unless asked for, and
   grouping by assignee, by due date, or both. "Today" and the weeks are the company's (the
   server's `company.local_today`), not the browser's, so a client in another time zone sees the
   same groups. Weeks run Monday to Sunday (the app's week, `domain.capacity.week_start`). */

export const COMPANY_TIME_ZONE = "America/Halifax";

export type TaskGrouping = "none" | "assignee" | "due" | "both";
export const GROUPINGS: [TaskGrouping, string][] = [
  ["none", "No grouping"],
  ["assignee", "By assignee"],
  ["due", "By due date"],
  ["both", "By assignee, then due date"],
];

export type DueBucket = "overdue" | "this_week" | "next_week" | "later" | "none";
export const DUE_BUCKETS: [DueBucket, string][] = [
  ["overdue", "Overdue"],
  ["this_week", "This week"],
  ["next_week", "Next week"],
  ["later", "Later"],
  ["none", "No date"],
];

/** Today's date (YYYY-MM-DD) in the company's time zone. */
export function companyToday(timeZone: string = COMPANY_TIME_ZONE, now: Date = new Date()): string {
  try {
    // en-CA formats as YYYY-MM-DD.
    return new Intl.DateTimeFormat("en-CA", {
      timeZone,
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
    }).format(now);
  } catch {
    return companyToday(COMPANY_TIME_ZONE, now);
  }
}

const dayNumber = (iso: string) => {
  const [y, m, d] = iso.split("-").map(Number) as [number, number, number];
  return Math.floor(Date.UTC(y, m - 1, d) / 86_400_000);
};

/** Days from Monday (0) to Sunday (6) for a YYYY-MM-DD date. */
const weekday = (iso: string) => (new Date(dayNumber(iso) * 86_400_000).getUTCDay() + 6) % 7;

/** The due-date group a date falls in, given today (both YYYY-MM-DD, company time). */
export function dueBucket(due: string | null | undefined, today: string): DueBucket {
  if (!due) return "none";
  const days = dayNumber(due) - dayNumber(today);
  if (days < 0) return "overdue";
  const untilSunday = 6 - weekday(today); // days left in this week, today included
  if (days <= untilSunday) return "this_week";
  if (days <= untilSunday + 7) return "next_week";
  return "later";
}

/** The tasks, not the stories (bugs and the rest never reach this list). */
export const onlyTasks = (tasks: Task[]) => tasks.filter((t) => t.kind === "task");

/** Done is what the portal already treats as done: the task's status. */
export const isDone = (t: Pick<Task, "status">) => t.status === "done";

export const assigneeOf = (t: Task) => (t.assigned_to_you ? "You" : (t.assignee_name ?? "CoreNode"));

/** You first, then your colleagues by name, CoreNode last. */
function assigneeOrder(names: string[]): string[] {
  const rank = (n: string) => (n === "You" ? 0 : n === "CoreNode" ? 2 : 1);
  return [...names].sort((a, b) => rank(a) - rank(b) || a.localeCompare(b));
}

export interface TaskGroup {
  id: string;
  label: string;
  tasks: Task[];
  /** Due-date groups inside an assignee group (grouping "both"). */
  groups?: TaskGroup[];
}

function byDue(tasks: Task[], today: string, prefix = ""): TaskGroup[] {
  const open = tasks.filter((t) => !isDone(t));
  const groups = DUE_BUCKETS.map(([key, label]) => ({
    id: `${prefix}${key}`,
    label,
    tasks: open.filter((t) => dueBucket(t.due_date, today) === key),
  }));
  // Finished tasks (shown on request) are not late or due: they get a group of their own, last.
  groups.push({ id: `${prefix}completed`, label: "Completed", tasks: tasks.filter(isDone) });
  return groups.filter((g) => g.tasks.length);
}

function byAssignee(tasks: Task[]): TaskGroup[] {
  const names = assigneeOrder([...new Set(tasks.map(assigneeOf))]);
  return names.map((n) => ({ id: `who-${n}`, label: n, tasks: tasks.filter((t) => assigneeOf(t) === n) }));
}

/** The groups for a grouping mode, empty groups left out; none for "No grouping". */
export function groupTasks(tasks: Task[], grouping: TaskGrouping, today: string): TaskGroup[] {
  if (grouping === "due") return byDue(tasks, today);
  if (grouping === "assignee") return byAssignee(tasks);
  if (grouping === "both")
    return byAssignee(tasks).map((g) => ({ ...g, groups: byDue(g.tasks, today, `${g.id}-`) }));
  return [];
}

const KEY = (userId: string) => `corecrm.portal.task-grouping.${userId}`;

/** The grouping a user picked last, from this browser's storage (unavailable storage: none). */
export function storedGrouping(userId: string): TaskGrouping {
  try {
    const v = localStorage.getItem(KEY(userId));
    return GROUPINGS.some(([g]) => g === v) ? (v as TaskGrouping) : "none";
  } catch {
    return "none";
  }
}

export function storeGrouping(userId: string, grouping: TaskGrouping): void {
  try {
    localStorage.setItem(KEY(userId), grouping);
  } catch {
    // not remembered
  }
}
